// KbTrainingHome.jsx — the branded KB-driven training home page.
//
// Renders one card per KB (call type), showing criterion count, total
// possible weight, and the threshold-score bar. Recert-required KBs are
// tagged with a gold pill + RotateCcw icon — same visual language as
// the TFVF home so users switching between the two modes recognise it.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, Circle, ArrowRight, RotateCcw, AlertTriangle, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const KbTrainingHome = ({ token }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/kb-training/me`, { headers: authHeaders(token) })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [token]);

  if (!data) {
    return (
      <div className="tf-card rounded-xl p-8 text-center" data-testid="kb-training-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--tf-forest)]" />
        <p className="text-sm text-[color:var(--tf-muted)] mt-2">Loading your training…</p>
      </div>
    );
  }
  if (data.error) {
    return (
      <div className="tf-card rounded-xl p-6 text-center">
        <p className="text-sm text-rose-600 font-semibold">Failed to load training</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kb-training-home">
      {/* Overall progress strip */}
      <div className="tf-card rounded-xl p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="tf-eyebrow">Your progress</p>
          <p className="text-sm font-semibold text-[color:var(--tf-forest)] tabular-nums">
            {data.acked_count} / {data.total_count} modules acknowledged
          </p>
        </div>
        <div className="h-2 rounded-full bg-[color:var(--tf-forest)]/10 overflow-hidden">
          <div
            className="h-full transition-all"
            style={{
              width: `${data.completion_pct}%`,
              background: 'var(--tf-forest)',
            }}
            data-testid="kb-training-progress-bar"
          />
        </div>
      </div>

      {/* Recert banner */}
      {data.recert_count > 0 && (
        <div
          className="rounded-xl p-4 flex items-start gap-3 border"
          style={{
            background: 'rgba(200,151,74,0.10)',
            borderColor: 'rgba(200,151,74,0.35)',
          }}
          data-testid="kb-training-recert-banner"
        >
          <AlertTriangle
            className="w-5 h-5 mt-0.5 shrink-0"
            style={{ color: 'var(--tf-gold-2)' }}
          />
          <div className="flex-1">
            <p className="text-sm font-semibold text-[color:var(--tf-forest)]">
              {data.recert_count === 1
                ? '1 knowledge base has been updated — please re-acknowledge'
                : `${data.recert_count} knowledge bases have been updated — please re-acknowledge`}
            </p>
            <p className="text-xs text-[color:var(--tf-muted)] mt-0.5">
              Criteria, weights, or the threshold changed since you last acknowledged. Open the module to see what&apos;s new.
            </p>
          </div>
        </div>
      )}

      {/* KB modules */}
      {data.modules.length === 0 ? (
        <div className="tf-card rounded-xl p-8 text-center" data-testid="kb-training-empty">
          <BookOpen className="w-10 h-10 mx-auto text-[color:var(--tf-forest)]/40 mb-2" />
          <p className="text-sm font-semibold text-[color:var(--tf-forest)]">
            No knowledge bases yet
          </p>
          <p className="text-xs text-[color:var(--tf-muted)] mt-1">
            Ask your admin to add a knowledge base for your team&apos;s call type.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="tf-eyebrow">Your knowledge bases · read + acknowledge each one</p>
          {data.modules.map((m) => {
            const acked = !!m.progress?.acked_at;
            const recert = !!m.progress?.recert_required;
            return (
              <Link
                key={m.id}
                to={`/training/kb/${m.id}`}
                className="tf-card rounded-lg p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
                style={
                  recert
                    ? { boxShadow: 'inset 0 0 0 1px rgba(200,151,74,0.55)' }
                    : undefined
                }
                data-testid={`kb-training-kb-${m.id}`}
              >
                <div className="w-8 shrink-0 text-center">
                  {recert ? (
                    <RotateCcw
                      className="w-6 h-6 mx-auto"
                      style={{ color: 'var(--tf-gold-2)' }}
                      data-testid={`kb-training-recert-icon-${m.id}`}
                    />
                  ) : acked ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto" />
                  ) : (
                    <Circle className="w-6 h-6 text-[color:var(--tf-forest)]/25 mx-auto" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="tf-eyebrow">{m.call_type?.replace(/_/g, ' ')}</span>
                    {recert && (
                      <span
                        className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded"
                        style={{
                          background: 'rgba(200,151,74,0.18)',
                          color: 'var(--tf-gold-2)',
                        }}
                        data-testid={`kb-training-recert-badge-${m.id}`}
                      >
                        Recert required
                      </span>
                    )}
                    <span className="text-[10px] text-[color:var(--tf-muted)] tabular-nums">
                      · {m.criteria_count} criteria · {m.total_weight} pts · pass ≥ {m.threshold_score}
                    </span>
                  </div>
                  <p className="text-base font-semibold text-[color:var(--tf-forest)] truncate">
                    {m.title}
                  </p>
                  {m.summary && (
                    <p className="text-xs text-[color:var(--tf-muted)] line-clamp-1">
                      {m.summary}
                    </p>
                  )}
                </div>
                <ArrowRight className="w-4 h-4 text-[color:var(--tf-forest)]/40 shrink-0" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KbTrainingHome;
